import React, { useEffect, useState } from "react";
import { ArrowLeft, Loader2, CalendarCheck, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import api from "@/lib/api";

// 9:00 AM → 6:00 PM in 30-min steps (latest start so 4hr job finishes by 10pm)
const ALL_TIMES = [];
for (let h = 9; h <= 18; h++) {
  ALL_TIMES.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 18) ALL_TIMES.push(`${String(h).padStart(2, "0")}:30`);
}

function to12h(t) {
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mStr} ${ampm}`;
}

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isDayFullyBooked(blockedSet) {
  const availableStarts = ALL_TIMES.filter((t) => {
    const mins = parseInt(t.split(":")[0], 10) * 60 + parseInt(t.split(":")[1], 10);
    return mins <= 18 * 60;
  });
  return availableStarts.every((t) => blockedSet.has(t));
}

export default function CalendarStep({ date, time, onChange, onConfirm, onBack, loading }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [selected, setSelected] = useState(date ? new Date(date + "T12:00:00") : null);
  const [chosenTime, setChosenTime] = useState(time || "");
  const [closedDates, setClosedDates] = useState([]);
  const [bookedTimes, setBookedTimes] = useState({});
  const [loadingAvail, setLoadingAvail] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoadingAvail(true);
      try {
        const [closedRes, availRes] = await Promise.all([
          api.get("/closed-dates"),
          api.get("/availability"),
        ]);
        setClosedDates(closedRes.data.closed_dates || []);
        setBookedTimes(availRes.data.blocked_slots || {});
      } catch {
        // silently fail
      } finally {
        setLoadingAvail(false);
      }
    };
    fetchAll();
  }, []);

  const isOutsideSeason = (d) => {
    const y = d.getFullYear();
    return d < new Date(y, 5, 18) || d > new Date(y, 8, 6);
  };

  const blockedForDate = selected
    ? new Set(bookedTimes[fmtDate(selected)] || [])
    : new Set();

  const isDateDisabled = (d) => {
    if (d < today) return true;
    if (isOutsideSeason(d)) return true;
    const ds = fmtDate(d);
    if (closedDates.includes(ds)) return true;
    const dayBlocked = new Set(bookedTimes[ds] || []);
    if (isDayFullyBooked(dayBlocked)) return true;
    return false;
  };

  useEffect(() => {
    if (chosenTime && blockedForDate.has(chosenTime)) {
      setChosenTime("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, bookedTimes]);

  const submit = () => {
    if (!selected) return toast.error("Pick a date");
    if (!chosenTime) return toast.error("Pick a time");
    onChange(fmtDate(selected), chosenTime);
    onConfirm(fmtDate(selected), chosenTime);
  };

  useEffect(() => {
    if (selected && chosenTime) onChange(fmtDate(selected), chosenTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, chosenTime]);

  const validTimes = ALL_TIMES.filter((t) => {
    const mins = parseInt(t.split(":")[0], 10) * 60 + parseInt(t.split(":")[1], 10);
    return mins <= 18 * 60;
  });
  const amTimes = validTimes.filter((t) => parseInt(t.split(":")[0], 10) < 12);
  const pmTimes = validTimes.filter((t) => parseInt(t.split(":")[0], 10) >= 12);

  const bookedDayModifier = (d) => {
    const ds = fmtDate(d);
    if (!bookedTimes[ds]) return false;
    return isDayFullyBooked(new Set(bookedTimes[ds]));
  };

  return (
    <div data-testid="step-calendar">
      <p className="overline mb-6">Step 06 / Schedule</p>
      <h2 className="font-display text-5xl sm:text-6xl leading-[0.95] mb-5 text-foreground">
        Pick a day,&nbsp;<br />
        <em className="italic text-foreground/85">pick a time.</em>
      </h2>
      <p className="text-muted mb-10 leading-relaxed">
        We work 9 AM to 6 PM daily, June 18 through September 6. Each detail takes around four hours.
      </p>

      <div className="grid lg:grid-cols-2 gap-6 items-stretch">
        <div className="card-luxe p-4">
          <p className="overline mb-3">Date</p>
          {loadingAvail ? (
            <div className="space-y-2">
              <div className="grid grid-cols-7 gap-2 mb-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <Skeleton key={i} className="h-3 w-full" />
                ))}
              </div>
              {Array.from({ length: 5 }).map((_, r) => (
                <div key={r} className="grid grid-cols-7 gap-2">
                  {Array.from({ length: 7 }).map((_, c) => (
                    <Skeleton key={c} className="h-10 w-full" />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <>
              <Calendar
                mode="single"
                selected={selected ?? undefined}
                onSelect={(d) => { setSelected(d ?? null); }}
                disabled={isDateDisabled}
                defaultMonth={new Date(today.getFullYear(), today.getMonth(), 1)}
                modifiers={{ fullyBooked: bookedDayModifier }}
                modifiersClassNames={{
                  fullyBooked: "rdp-fully-booked",
                }}
                className="bg-transparent text-foreground"
                data-testid="calendar-date-picker"
              />
              <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-faint">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-foreground/25" />
                  Fully booked
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-foreground/40" />
                  Unavailable
                </span>
              </div>
            </>
          )}
        </div>

        <div className="card-luxe p-6 flex flex-col h-full">
          <p className="overline mb-3">Available Times</p>
          {!selected ? (
            <p className="text-muted text-sm">Pick a date first.</p>
          ) : loadingAvail ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-5 overflow-y-auto pr-1 flex-1">
              <TimeGroup label="Morning" times={amTimes} chosen={chosenTime} onPick={setChosenTime} blocked={blockedForDate} />
              <TimeGroup label="Afternoon" times={pmTimes} chosen={chosenTime} onPick={setChosenTime} blocked={blockedForDate} />
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 tile-spec p-5 flex gap-4 items-start rounded-xl">
        <Clock size={18} className="text-foreground/70 mt-0.5 shrink-0" />
        <p className="text-sm text-foreground/80 leading-relaxed">
          Each appointment is approximately{" "}
          <span className="text-foreground font-medium">4 hours</span>. The latest start time is{" "}
          <span className="text-foreground font-medium">6:00 PM</span> so we finish by 10 PM.
          Booked slots and fully-booked days are unavailable.
        </p>
      </div>

      <div className="mt-12 flex justify-between gap-3">
        <button className="btn-secondary" onClick={onBack} disabled={loading} data-testid="calendar-back-button">
          <ArrowLeft size={14} /> Back
        </button>
        <button
          className="btn-primary"
          onClick={submit}
          disabled={loading || !selected || !chosenTime}
          data-testid="calendar-confirm-button"
        >
          {loading ? (
            <><Loader2 size={14} className="animate-spin" /> Confirming…</>
          ) : (
            <>Confirm Booking <CalendarCheck size={14} /></>
          )}
        </button>
      </div>
    </div>
  );
}

function TimeGroup({ label, times, chosen, onPick, blocked }) {
  if (times.length === 0) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-faint mb-2">{label}</p>
          <div className="grid grid-cols-3 gap-2">
            {times.map((t) => {
              const active = chosen === t;
              const isBlocked = blocked.has(t);
              return (
                <button
                  key={t}
                  onClick={() => !isBlocked && onPick(t)}
                  disabled={isBlocked}
                  className={`px-2 py-2.5 text-[13px] font-medium rounded-lg transition-all ${
                    isBlocked
                      ? "text-foreground/20 cursor-not-allowed line-through"
                      : active
                      ? "bg-foreground text-background"
                      : "text-foreground/80 hover:bg-foreground/[0.04]"
                  }`}
                  style={
                    isBlocked
                      ? { background: "rgba(245,245,247,0.025)", textDecorationColor: "rgba(245,245,247,0.25)" }
                      : active
                        ? { background: "var(--text)", color: "var(--bg)" }
                        : undefined
                  }
                  data-testid={`time-slot-${t}`}
                >
                  {to12h(t)}
                </button>
              );
            })}
          </div>
    </div>
  );
}
